import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import AdminLayout from '@/layouts/AdminLayout.vue'
import { useAuthStore } from '@/stores/auth'

// Role constants for route meta
const ALL_ADMIN_ROLES = [
  'SuperAdmin',
  'CustomerService',
  'SalesMarketing',
  'ChannelPartner',
  'DealSponsor',
  'ComplianceOfficer'
] as const

const TRANSACTION_ROLES = [
  'SuperAdmin',
  'CustomerService',
  'SalesMarketing',
  'ChannelPartner'
] as const

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: '/admin'
  },
  {
    path: '/admin',
    component: AdminLayout,
    children: [
      {
        path: '',
        name: 'Dashboard',
        component: () => import('@/screens/admin/AdminDashboardScreen.vue'),
        meta: { requiredRole: ALL_ADMIN_ROLES }
      },
      {
        path: 'users',
        name: 'Users',
        component: () => import('@/screens/admin/UsersScreen.vue'),
        meta: { requiredRole: ['SuperAdmin', 'CustomerService'] }
      },
      {
        path: 'staff',
        name: 'AdminStaff',
        component: () => import('@/screens/admin/AdminStaffScreen.vue'),
        meta: { requiredRole: ['SuperAdmin'] }
      },
      {
        path: 'create-deal',
        name: 'CreateDeal',
        component: () => import('@/screens/admin/CreateDealScreen.vue'),
        meta: { requiredRole: ['SuperAdmin', 'ChannelPartner'] }
      },
      {
        path: 'deals',
        name: 'Deals',
        component: () => import('@/screens/admin/DealsScreen.vue'),
        meta: { requiredRole: TRANSACTION_ROLES }
      },
      {
        path: 'transactions',
        name: 'Transactions',
        component: () => import('@/screens/admin/TransactionsScreen.vue'),
        meta: { requiredRole: TRANSACTION_ROLES }
      },
      {
        path: 'audit-logs',
        name: 'AuditLogs',
        component: () => import('@/screens/admin/AuditLogsScreen.vue'),
        meta: { requiredRole: ['SuperAdmin'] }
      },
      {
        path: 'collections',
        name: 'Collections',
        component: () => import('@/screens/admin/CollectionsScreen.vue'),
        meta: { requiredRole: ['SuperAdmin'] }
      },
      {
        path: 'collection/:name',
        name: 'CollectionDetail',
        component: () => import('@/screens/admin/CollectionDetailScreen.vue'),
        meta: { requiredRole: ['SuperAdmin'] }
      },
      {
        path: 'epicai-docs',
        name: 'EpicaiDocs',
        component: () => import('@/screens/admin/EpicaiDocsScreen.vue'),
        meta: { requiredRole: ['SuperAdmin'] }
      },
      {
        path: 'profile-tools',
        name: 'ProfileTools',
        component: () => import('@/screens/admin/ProfileToolsScreen.vue'),
        meta: { requiredRole: ['SuperAdmin'] }
      },
      // DealSponsor self-service routes
      {
        path: 'profile',
        name: 'Profile',
        component: () => import('@/screens/admin/ProfileScreen.vue'),
        meta: { requiredRole: ['DealSponsor', 'ComplianceOfficer'] }
      },
      {
        path: 'my-deals',
        name: 'MyDeals',
        component: () => import('@/screens/admin/MyDealsScreen.vue'),
        meta: { requiredRole: ['DealSponsor'] }
      },
      {
        path: 'my-transactions',
        name: 'MyTransactions',
        component: () => import('@/screens/admin/MyTransactionsScreen.vue'),
        meta: { requiredRole: ['DealSponsor'] }
      },
      // ComplianceOfficer routes
      {
        path: 'compliance/dashboard',
        name: 'ComplianceDashboard',
        component: () => import('@/screens/admin/ComplianceDashboardScreen.vue'),
        meta: { requiredRole: ['ComplianceOfficer'] }
      },
      {
        path: 'compliance/athletes',
        name: 'ComplianceAthletes',
        component: () => import('@/screens/admin/ComplianceAthletesScreen.vue'),
        meta: { requiredRole: ['ComplianceOfficer'] }
      },
      {
        path: 'compliance/deals',
        name: 'ComplianceDeals',
        component: () => import('@/screens/admin/ComplianceDealsScreen.vue'),
        meta: { requiredRole: ['ComplianceOfficer'] }
      },
      {
        path: 'compliance/reports',
        name: 'ComplianceReports',
        component: () => import('@/screens/admin/ComplianceReportsScreen.vue'),
        meta: { requiredRole: ['ComplianceOfficer'] }
      }
    ]
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// Route guard to check admin role
router.beforeEach(async (to, from, next) => {
  const auth = useAuthStore()

  // Allow health check (for CI/CD)
  if (to.path === '/health') {
    next()
    return
  }

  // Wait for auth initialization if not yet initialized
  if (!auth.isInitialized) {
    // This should rarely happen since main.ts primes the auth store
    // But if it does, wait a bit for initialization
    await new Promise(resolve => setTimeout(resolve, 100))
  }

  // Check authentication (auth store already primed on startup)
  if (!auth.isAuthenticated) {
    // Redirect to Entra SSO login
    window.location.href = '/api/admin/auth/login'
    next(false) // Cancel navigation so router doesn't hang
    return
  }

  // Check role authorization (memoized in auth store)
  const requiredRoles = to.meta.requiredRole as string[] | undefined
  if (requiredRoles && !auth.hasAnyRole(requiredRoles)) {
    // Forbidden - redirect to dashboard
    next('/admin')
    return
  }

  next()
})

export default router
